export function toObject<T, K extends keyof any, V>(array: Iterable<T>, keySelector: (item: T) => K, selector: (item: T) => V): Record<K, V> {
  const obj: Record<any, V> = {}

  for (const item of array) {
    const key = keySelector(item)
    obj[key as any] = selector(item)
  }

  return obj
}

export function mapObject<V, T>(obj: Record<string, V>, onItem: (key: string, value: V) => T) {
  const res: T[] = []
  for (const key of Object.keys(obj)) {
    res.push(onItem(key, obj[key]))
  }
  return res
}


type Comparer<T> = (a: T, b: T) => number
type PropGetter<T> = (obj: T) => any

const comparer = <T>(fn: PropGetter<T>, desc: boolean, prev?: Comparer<T>) => (a: T, b: T) => {
  if (prev) {
    const res = prev(a, b)
    if (res !== 0) { return res }
  }

  const pa = fn(a)
  const pb = fn(b)
  const p = desc ? -1 : 1

  if (pa < pb) {
    return -p
  } else if (pa > pb) {
    return p
  } else {
    return 0
  }
}

interface OrderBy<T> extends Comparer<T> {
  thenBy(fn: PropGetter<T>): OrderByWithDesc<T>
}

interface OrderByWithDesc<T> extends OrderBy<T> {
  desc(): OrderBy<T>
}

function withThenBy<T>(fn: PropGetter<T>, prev?: Comparer<T>): OrderByWithDesc<T> {
  let comp = comparer(fn, false, prev)
  const compWithDesc: Comparer<T> = (a, b) => comp(a, b)

  const res = Object.assign(compWithDesc, {
    thenBy(fn: PropGetter<T>) {
      return withThenBy(fn, compWithDesc)
    },
    desc() {
      comp = comparer(fn, true, prev)
      return res;
    },
  })

  return res
}

export function orderBy<T>(fn: PropGetter<T>) {
  return withThenBy(fn)
}

export function flatten<T>(arrays: T[][]) {
  return new Array<T>().concat(...arrays)
}

export function distinct<T>(array: T[]): T[] {
  return [ ...new Set(array) ]
}

export function mergeObjects<V extends any>(objects: Record<string, V>[], merge: (values: V[], key: string) => V): Record<string, V> {
  const result: Record<string, V> = {}
  const keys = distinct(flatten(objects.map(Object.keys)))
  for (const key of keys) {
    result[key] = merge(objects.map(o => o[key]), key)
  }
  return result
}

export function arrayShallowEqual<T>(a: T[], b: T[]) {
  return a === b || (a.length === 0 && b.length === 0) || a.length === b.length && a.every((ai, i) => ai === b[i])
}

export function createArrayCache<T>() {
  const cache = new Map<string, T[]>()
  return function (next: T[], key: string): T[] {
    const prev = cache.get(key)
    if (prev && arrayShallowEqual(prev, next)) {
      return prev
    } else {
      cache.set(key, next)
      return next
    }
  }
}

